/**
 * Onboarding Wizard Overlay
 * Global overlay component for new user setup wizard
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Rocket, ChevronRight, ChevronLeft, X, Pause, Play,
  CheckCircle2, Circle, ExternalLink, Video, Info,
  Sparkles, Target, ArrowRight, Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Rhino-1 Assistant Avatar
const RhinoAvatar = () => (
  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0">
    <Rocket className="h-5 w-5 text-white" />
  </div>
);

// Step type icons
const STEP_ICONS = {
  NAVIGATION: Target,
  ACTION: Sparkles,
  VIDEO: Video,
  INFO: Info,
  COMING_SOON: Clock
};

// Placement mappings for positioning the bubble
const PLACEMENT_STYLES = {
  TOP: { bottom: '100%', left: '50%', transform: 'translateX(-50%) translateY(-16px)' },
  BOTTOM: { top: '100%', left: '50%', transform: 'translateX(-50%) translateY(16px)' },
  LEFT: { right: '100%', top: '50%', transform: 'translateY(-50%) translateX(-16px)' },
  RIGHT: { left: '100%', top: '50%', transform: 'translateY(-50%) translateX(16px)' },
  TOP_LEFT: { bottom: '100%', left: '0', transform: 'translateY(-16px)' },
  TOP_RIGHT: { bottom: '100%', right: '0', transform: 'translateY(-16px)' },
  BOTTOM_LEFT: { top: '100%', left: '0', transform: 'translateY(16px)' },
  BOTTOM_RIGHT: { top: '100%', right: '0', transform: 'translateY(16px)' },
  CENTER: { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
};

// Onboarding Bubble Component
const OnboardingBubble = ({
  step,
  currentIndex,
  totalSteps,
  onNext,
  onPrev,
  onPause,
  onSkip,
  onComplete,
  targetElement,
  isLastStep
}) => {
  const StepIcon = STEP_ICONS[step?.step_type] || Info;
  const placement = step?.placement || 'CENTER';
  const isCentered = !targetElement || placement === 'CENTER';
  
  return (
    <Card 
      className={`w-[400px] max-w-[90vw] shadow-2xl border-2 border-blue-200 dark:border-blue-800 bg-white dark:bg-slate-900 z-[10001] ${
        isCentered ? 'fixed' : 'absolute'
      }`}
      style={isCentered ? PLACEMENT_STYLES.CENTER : PLACEMENT_STYLES[placement]}
      data-testid="onboarding-bubble"
    >
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <RhinoAvatar />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="text-xs mb-1">
                <StepIcon className="h-3 w-3 mr-1" />
                Step {currentIndex + 1} of {totalSteps}
              </Badge>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 -mr-2 -mt-1"
                onClick={onPause}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <CardTitle className="text-lg leading-tight">{step?.title}</CardTitle>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 pb-4">
        {/* Progress bar */}
        <div className="mb-4">
          <Progress 
            value={((currentIndex + 1) / totalSteps) * 100} 
            className="h-1.5 [&>div]:bg-blue-500"
          />
        </div>
        
        {/* Step content */}
        <div className="text-sm text-muted-foreground whitespace-pre-wrap">
          {step?.body_text}
        </div>
        
        {/* Video embed */}
        {step?.step_type === 'VIDEO' && step?.media_url && (
          <div className="mt-4">
            {step.media_type === 'YOUTUBE' ? (
              <div className="aspect-video rounded-lg overflow-hidden bg-black">
                <iframe
                  src={step.media_url.replace('watch?v=', 'embed/')}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : step.media_type === 'EXTERNAL_LINK' ? (
              <Button variant="outline" className="w-full" asChild>
                <a href={step.media_url} target="_blank" rel="noopener noreferrer">
                  <Video className="h-4 w-4 mr-2" />
                  Watch Video
                  <ExternalLink className="h-3 w-3 ml-2" />
                </a>
              </Button>
            ) : null}
          </div>
        )}
        
        {/* Coming Soon badge */}
        {step?.is_coming_soon && (
          <Badge className="mt-3 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            <Clock className="h-3 w-3 mr-1" />
            Coming Soon
          </Badge>
        )}
        
        {/* Go to page button */}
        {step?.target_route && !targetElement && (
          <Button 
            variant="outline" 
            className="w-full mt-4"
            onClick={() => window.location.href = step.target_route}
          >
            Go to this page
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </CardContent>
      
      <CardFooter className="flex items-center justify-between pt-0 border-t">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onPause}
            className="text-muted-foreground"
          >
            <Pause className="h-4 w-4 mr-1" />
            Pause
          </Button>
          {currentIndex > 0 && !isLastStep && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onSkip}
              className="text-muted-foreground"
            >
              Skip
            </Button>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {currentIndex > 0 && (
            <Button variant="outline" size="sm" onClick={onPrev}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          )}
          <Button size="sm" onClick={isLastStep ? onComplete : onNext}>
            {isLastStep ? (
              <>
                <CheckCircle2 className="h-4 w-4 mr-1" />
                Finish
              </>
            ) : (
              <>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

// Main Onboarding Wizard Overlay
export const OnboardingWizardOverlay = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isActive, setIsActive] = useState(false);
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(null);
  const [targetElement, setTargetElement] = useState(null);
  const [targetRect, setTargetRect] = useState(null);
  
  const observerRef = useRef(null);

  // Initial check for onboarding
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!user?.id) return;
      
      try {
        const response = await axios.get(`${API}/onboarding/should-show?user_id=${user.id}`);
        const data = response.data;
        
        if (data.should_show_wizard) {
          // Start or resume onboarding
          const startRes = await axios.post(`${API}/onboarding/start?user_id=${user.id}`);
          setSteps(startRes.data.steps);
          setProgress(startRes.data.progress);
          setCurrentStep(startRes.data.current_step);
          
          // Find current index
          const idx = startRes.data.steps.findIndex(s => s.id === startRes.data.current_step?.id);
          setCurrentIndex(idx >= 0 ? idx : 0);
          setIsActive(true);
        }
      } catch (error) {
        console.error('Error checking onboarding:', error);
      }
    };
    
    checkOnboarding();
  }, [user?.id]);

  // Find and highlight target element
  useEffect(() => {
    if (!isActive || !currentStep?.target_selector) {
      setTargetElement(null);
      setTargetRect(null);
      return;
    }

    const findTarget = () => {
      const selector = `[data-onboard="${currentStep.target_selector}"]`;
      const element = document.querySelector(selector);
      
      if (element) {
        setTargetElement(element);
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
        
        // Scroll into view if needed
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        setTargetElement(null);
        setTargetRect(null);
      }
    };

    // Initial find
    findTarget();

    // Watch for DOM changes
    observerRef.current = new MutationObserver(findTarget);
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Re-check on resize
    window.addEventListener('resize', findTarget);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      window.removeEventListener('resize', findTarget);
    };
  }, [isActive, currentStep?.target_selector, location.pathname]);

  // Handle step navigation
  const handleNext = async () => {
    if (!currentStep) return;
    
    try {
      const response = await axios.post(
        `${API}/onboarding/complete-step?user_id=${user.id}`,
        { step_id: currentStep.id }
      );
      
      if (response.data.is_complete) {
        toast.success('🎉 Onboarding complete! Welcome to Rhino-1!');
        setIsActive(false);
      } else {
        setCurrentStep(response.data.next_step);
        setCurrentIndex(prev => prev + 1);
        setProgress(response.data.progress);
        
        // Navigate to target route if specified
        if (response.data.next_step?.target_route) {
          navigate(response.data.next_step.target_route);
        }
      }
    } catch (error) {
      console.error('Error completing step:', error);
      toast.error('Failed to save progress');
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      const prevStep = steps[currentIndex - 1];
      setCurrentStep(prevStep);
      setCurrentIndex(prev => prev - 1);
      
      if (prevStep?.target_route) {
        navigate(prevStep.target_route);
      }
    }
  };

  const handlePause = async () => {
    try {
      await axios.post(`${API}/onboarding/pause?user_id=${user.id}`);
      setIsActive(false);
      toast.info('Onboarding paused. You can resume anytime!');
    } catch (error) {
      console.error('Error pausing onboarding:', error);
    }
  };

  const handleSkip = async () => {
    // Skip current step without completing it
    if (currentIndex < steps.length - 1) {
      const nextStep = steps[currentIndex + 1];
      setCurrentStep(nextStep);
      setCurrentIndex(prev => prev + 1);
      
      if (nextStep?.target_route) {
        navigate(nextStep.target_route);
      }
    }
  };

  const handleComplete = async () => {
    try {
      await axios.post(`${API}/onboarding/complete?user_id=${user.id}`);
      setIsActive(false);
      toast.success('🎉 Onboarding complete! Welcome to Rhino-1!');
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  };

  if (!isActive || !currentStep) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[10000]" data-testid="onboarding-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      
      {/* Highlight cutout for target element */}
      {targetRect && (
        <>
          {/* Create a "spotlight" effect */}
          <div
            className="absolute bg-transparent"
            style={{
              top: targetRect.top - 8,
              left: targetRect.left - 8,
              width: targetRect.width + 16,
              height: targetRect.height + 16,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
              borderRadius: '8px',
              zIndex: 10000
            }}
          />
          {/* Highlight border */}
          <div
            className="absolute border-2 border-blue-500 rounded-lg animate-pulse"
            style={{
              top: targetRect.top - 8,
              left: targetRect.left - 8,
              width: targetRect.width + 16,
              height: targetRect.height + 16,
              zIndex: 10000
            }}
          />
        </>
      )}
      
      {/* Bubble positioned relative to target or centered */}
      {targetRect ? (
        <div
          className="absolute"
          style={{
            top: targetRect.top + targetRect.height / 2,
            left: targetRect.left + targetRect.width / 2,
            zIndex: 10001
          }}
        >
          <OnboardingBubble
            step={currentStep}
            currentIndex={currentIndex}
            totalSteps={steps.length}
            onNext={handleNext}
            onPrev={handlePrev}
            onPause={handlePause}
            onSkip={handleSkip}
            onComplete={handleComplete}
            targetElement={targetElement}
            isLastStep={currentIndex === steps.length - 1}
          />
        </div>
      ) : (
        <OnboardingBubble
          step={currentStep}
          currentIndex={currentIndex}
          totalSteps={steps.length}
          onNext={handleNext}
          onPrev={handlePrev}
          onPause={handlePause}
          onSkip={handleSkip}
          onComplete={handleComplete}
          targetElement={null}
          isLastStep={currentIndex === steps.length - 1}
        />
      )}
    </div>
  );
};

export default OnboardingWizardOverlay;
